<template>
  <section>
    <h4 class="text-h4 mb-4">
      {{ t('shipment.outgoingPackages') }}
    </h4>

    <VCard>
      <!-- Filters and Actions -->
      <VCardText>
        <div class="d-flex align-center flex-wrap gap-2 mb-4">
          <VSpacer />

          <VTextField
            v-model="search"
            :label="t('filters.search')"
            hide-details
            density="compact"
            prepend-icon="tabler-search"
            class="me-2"
            style="max-width: 250px;"
          />
        </div>

        <!-- Filter Section -->
        <VExpansionPanels
          v-model="expandedPanels"
          variant="accordion"
        >
          <VExpansionPanel>
            <VExpansionPanelTitle>
              {{ t('filters.advancedFilters') }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-wrap gap-4 align-center">
                <!-- Status Filter -->
                <VSelect
                  v-model="filters.status"
                  :items="statusOptions"
                  :label="t('filters.status')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  clearable
                  style="min-width: 220px;"
                />

                <!-- Team Filter -->
                <VSelect
                  v-model="filters.team_id"
                  :items="teamOptions"
                  item-title="title"
                  item-value="value"
                  :label="t('filters.team')"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                  class="filter-select"
                  clearable
                  style="min-width: 150px;"
                >
                  <template #selection="{ item }">
                    <VChip
                      :color="getTeamBadgeColor(item.raw.code)"
                      size="small"
                    >
                      {{ item.title }} ({{ item.raw.code }})
                    </VChip>
                  </template>
                  
                  <template #item="{ item, props }">
                    <VListItem v-bind="props">
                      <template #prepend>
                        <VChip
                          :color="getTeamBadgeColor(item.raw.code)"
                          size="small"
                        >
                          {{ item.raw.code }}
                        </VChip>
                      </template>
                    </VListItem>
                  </template>
                </VSelect>

                <!-- Transport Filter -->
                <VSelect
                  v-model="filters.transport"
                  :items="transportOptions"
                  :label="t('filters.transport')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  style="min-width: 150px;"
                />

                <!-- Date Preset Selector -->
                <VSelect
                  v-model="filters.date_preset"
                  :items="datePresets"
                  :label="t('filters.datePreset')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  style="min-width: 160px;"
                />

                <!-- Custom Date Range (only shown when preset is 'custom') -->
                <div
                  v-if="filters.date_preset === 'custom'"
                  class="date-range-filter d-flex gap-2"
                  style="min-width: 360px;"
                >
                  <VTextField
                    v-model="filters.start_date"
                    :label="t('filters.startDate')"
                    type="date"
                    density="compact"
                    hide-details
                    class="filter-date"
                  />
                  <VTextField
                    v-model="filters.end_date"
                    :label="t('filters.endDate')"
                    type="date"
                    density="compact"
                    hide-details
                    class="filter-date"
                  />
                </div>

                <!-- Reset Filters Button -->
                <VBtn
                  color="secondary"
                  variant="outlined"
                  size="small"
                  prepend-icon="tabler-refresh"
                  @click="resetFilters"
                >
                  {{ t('filters.reset') }}
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <!-- Data Table -->
      <VDataTableServer
        v-model="selectedRows"
        v-model:sort-by="tableSort"
        :headers="headers"
        :items="packages"
        :loading="loading"
        :items-length="packagesMeta?.total || 0"
        item-value="id"
        return-object
        show-select
        :items-per-page="itemsPerPage"
        :page="currentPage"
        @update:options="handleTableOptionsUpdate"
      >
        <!-- Custom rendering for image column -->
        <template #item.image="{ item }">
          <div class="d-flex justify-center">
            <div
              class="image-cell cursor-pointer"
              @click="handleImageClick(item)"
            >
              <!-- Show package image if available, otherwise show default -->
              <img
                v-if="item.custom_image_url && !item.custom_image_url.includes('default.png')"
                :src="item.custom_image_url"
                alt="Package image"
                width="40"
                height="40"
                class="rounded"
                @error="e => e.target.src = '/img/default.png'"
              >
              <div
                v-else
                class="d-flex align-center justify-center"
              >
                <img
                  src="/img/default.png"
                  alt="Upload image"
                  width="40"
                  height="40"
                  class="rounded opacity-75"
                >
                <VIcon
                  class="upload-icon"
                  size="x-small"
                  color="primary"
                >
                  tabler-upload
                </VIcon>
              </div>
            </div>
          </div>
        </template>

        <!-- Custom rendering for date column -->
        <template #item.date="{ item }">
          <span>{{ formatDate(item.date) }}</span>
        </template>

        <!-- Custom rendering for barcode column -->
        <template #item.barcode="{ item }">
          <div class="d-flex align-center">
            <span>{{ item.barcode || item.sku }}</span>
            <small
              v-if="item.marking"
              class="ml-2"
            >({{ item.marking }})</small>
          </div>
        </template>

        <!-- Custom rendering for parcels column -->
        <template #item.parcels="{ item }">
          <div v-if="item.incoming_parcels && item.incoming_parcels.length">
            <div
              v-for="parcel in item.incoming_parcels"
              :key="parcel.id"
              class="mb-1"
            >
              <span>{{ parcel.barcode }}</span>
              <small
                v-if="parcel.customer"
                class="ml-1"
              >(<CustomerLink :customer="parcel.customer" :show-client-id="false" />)</small>
            </div>
          </div>
          <span v-else>-</span>
        </template>
        
        <!-- Custom rendering for products column -->
        <template #item.products="{ item }">
          <div class="products-cell">
            {{ getLocalizedProductsDisplayText(item) }}
          </div>
        </template>

        <!-- Custom rendering for team column -->
        <template #item.team="{ item }">
          <VChip
            v-if="item.team?.name"
            :color="getTeamBadgeColor(item.team.code)"
            size="small"
          >
            {{ item.team.name }} ({{ item.team.code }})
          </VChip>
          <VChip
            v-else-if="item.team_id && teamOptions.length > 0"
            :color="getTeamBadgeColor(getTeamCodeById(item.team_id))"
            size="small"
          >
            {{ getTeamNameById(item.team_id) }} ({{ getTeamCodeById(item.team_id) }})
          </VChip>
          <VChip
            v-else
            :color="item.belongs_to === 0 ? 'info' : item.belongs_to === 1 ? 'success' : 'warning'"
            size="small"
            class="text-uppercase"
          >
            {{ getLegacyTeamName(item.belongs_to) }} ({{ getLegacyTeamCode(item.belongs_to) }})
          </VChip>
        </template>

        <!-- Custom rendering for transport column -->
        <template #item.transport="{ item }">
          <VChip
            :color="item.transport === 0 ? 'info' : 'primary'"
            size="small"
          >
            {{ item.transport === 0 ? t('shipment.by_plane') : t('shipment.by_ship') }}
          </VChip>
        </template>

        <!-- Custom rendering for status column -->
        <template #item.status="{ item }">
          <VChip
            :color="item.status_color || getStatusColor(item.status)"
            size="small"
          >
            <VIcon
              v-if="item.status_icon"
              :icon="item.status_icon"
              size="x-small"
              class="me-1"
            />
            {{ t(`shipment.statuses.${item.status_label || item.status}`, item.status_label) }}
          </VChip>
        </template>

        <!-- Actions column with multiple buttons -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-1">
            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  icon
                  variant="text"
                  color="primary"
                  size="small"
                  v-bind="props"
                  @click="viewPackage(item)"
                >
                  <VIcon>tabler-eye</VIcon>
                </VBtn>
              </template>
              {{ t('actions.view') }}
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  v-if="canEditPackage"
                  icon
                  variant="text"
                  color="warning"
                  size="small"
                  v-bind="props"
                  @click="router.push(`/shipment/outgoing-packages/${item.id}/edit`)"
                >
                  <VIcon>tabler-edit</VIcon>
                </VBtn>
              </template>
              {{ t('actions.edit') }}
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  icon
                  variant="text"
                  color="info"
                  size="small"
                  v-bind="props"
                  @click="printLabel(item.id)"
                >
                  <VIcon>tabler-printer</VIcon>
                </VBtn>
              </template>
              {{ t('actions.printLabel') }}
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  v-if="canDeletePackage"
                  icon
                  variant="text"
                  color="error"
                  size="small"
                  v-bind="props"
                  @click="confirmDelete(item)"
                >
                  <VIcon>tabler-trash</VIcon>
                </VBtn>
              </template>
              {{ t('actions.delete') }}
            </VTooltip>
          </div>
        </template>
      </VDataTableServer>

      <!-- Batch Actions Bar -->
      <VCardText v-if="selectedRows.length > 0">
        <div class="d-flex justify-space-between align-center">
          <div class="d-flex gap-2">
            <VBtn
              v-if="canCreatePackingList"
              color="primary"
              :disabled="!selectedRows.length"
              prepend-icon="tabler-package-export"
              size="small"
              @click="createPackingList"
            >
              {{ t('actions.createPackingList', 'Create Packing List') }}
            </VBtn>

            <VBtn
              v-if="canCreatePackingList"
              color="secondary"
              :disabled="!selectedRows.length"
              prepend-icon="tabler-package-import"
              size="small"
              @click="showAddToPackingListDialog = true"
            >
              {{ t('actions.addToPackingList', 'Add to Packing List') }}
            </VBtn>

            <VBtn
              color="info"
              :disabled="!selectedRows.length"
              prepend-icon="tabler-printer"
              size="small"
              @click="printSelectedLabels"
            >
              {{ t('actions.printLabels') }}
            </VBtn>

            <VBtn
              v-if="canDeletePackage"
              color="error"
              :disabled="!selectedRows.length"
              prepend-icon="tabler-trash"
              size="small"
              @click="confirmMassDelete"
            >
              {{ t('actions.deleteSelected') }}
            </VBtn>
          </div>

          <div>
            {{ t('shipment.selectedItems', { count: selectedRows.length }) }}
            <span v-if="selectedRows.length > 0" class="text-caption text-grey ms-2">
              ({{ selectedRows.map(row => `#${row.id}`).join(', ') }})
            </span>
          </div>
        </div>
      </VCardText>

      <!-- Package View Dialog -->
      <PackageViewModal
        v-model="viewDialogOpen"
        :package="selectedPackageForView"
        :can-delete="canDeletePackage"
        :can-edit="canEditPackage"
        @request-upload-image="handleUploadImageRequest"
      />

      <!-- Confirm Delete Dialog -->
      <VDialog
        v-model="deleteDialog.open"
        max-width="500"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('messages.confirmDeleteTitle') }}
          </VCardTitle>
          <VCardText>
            {{ t('messages.confirmDeletePackage') }}
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="deleteDialog.open = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="error"
              :loading="deleteDialog.loading"
              @click="deletePackage"
            >
              {{ t('actions.delete') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- Mass Delete Confirmation Dialog -->
      <VDialog
        v-model="showConfirmDeleteDialog"
        max-width="500"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('messages.confirmDeleteTitle') }}
          </VCardTitle>
          <VCardText>
            {{ t('messages.confirmDeleteMultiple', { count: selectedRows.length }) }}
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="showConfirmDeleteDialog = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="error"
              @click="handleDeleteSelected"
            >
              {{ t('actions.delete') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- Image Upload Dialog -->
      <VDialog
        v-model="showUploadImageDialog"
        max-width="500"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('shipment.uploadImage') }}
            <span
              v-if="selectedPackageForView"
              class="text-subtitle-1 ml-2"
            >
              ({{ t('shipment.package') }} #{{ selectedPackageForView.id || '' }})
            </span>
          </VCardTitle>
          <VCardText>
            <p class="mb-4">
              {{ t('shipment.uploadImageDescription') }}
            </p>

            <VAlert
              v-if="!selectedPackageForView"
              type="warning"
              dense
            >
              {{ t('messages.selectPackageFirst', 'Please select a package first') }}
            </VAlert>

            <div v-else>
              <VFileInput
                v-model="uploadedImage"
                :label="t('shipment.selectImage')"
                accept="image/*"
                prepend-icon="tabler-camera"
                :hint="t('shipment.imageHint', 'Select an image (max 2MB)')"
                persistent-hint
                :disabled="!selectedPackageForView"
                @change="handleImageUpload"
              />

              <!-- Preview uploaded image -->
              <div
                v-if="uploadedImage"
                class="d-flex justify-center mt-4"
              >
                <div class="image-preview">
                  <img
                    :src="URL.createObjectURL(uploadedImage)"
                    width="150"
                    height="150"
                    class="rounded"
                  >
                </div>
              </div>
            </div>
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="showUploadImageDialog = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="primary"
              :loading="uploadingImage"
              :disabled="!uploadedImage || !selectedPackageForView"
              @click="uploadImage"
            >
              {{ t('actions.upload') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <PrintPreviewModal
        v-model:visible="showPrintPreview"
        :title="printTitle"
        :selected-items="printSelectedItems"
        :table-columns="printTableColumns"
        show-total-weight
        show-summary
        :reference-number="`OUT-${new Date().getFullYear()}-${printSelectedItems.length > 1 ? printSelectedItems.length : printSelectedItems[0]?.id || ''}`"
        company-name="Godana Service"
        class="outgoing-packages-print-modal"
        @print="handlePrint"
      />

      <!-- Add to Packing List Dialog -->
      <VDialog
        v-model="showAddToPackingListDialog"
        max-width="600"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('actions.addToPackingList', 'Add to Packing List') }}
          </VCardTitle>
          <VCardText>
            <p class="mb-4">
              {{ t('messages.selectPackingList', `Select a packing list to add ${selectedRows.length} package(s)`) }}
            </p>
            <VAutocomplete
              v-model="selectedPackingListId"
              :items="packingListOptions"
              :loading="loadingPackingLists"
              item-title="label"
              item-value="value"
              :label="t('shipment.packingList', 'Packing List')"
              density="comfortable"
              variant="outlined"
              clearable
            >
              <template #item="{ item, props }">
                <VListItem
                  v-bind="props"
                  :title="item.raw.label"
                  :subtitle="item.raw.date"
                >
                  <template #prepend>
                    <VIcon icon="tabler-package" />
                  </template>
                </VListItem>
              </template>
            </VAutocomplete>
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="showAddToPackingListDialog = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="primary"
              :loading="addingToPackingList"
              :disabled="!selectedPackingListId"
              @click="addToExistingPackingList"
            >
              {{ t('actions.add', 'Add') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </VCard>
  </section>
</template>

<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Packages',
  },
})

import { ref, onMounted, computed, watch, reactive, onUnmounted, nextTick } from 'vue'
import { useOutgoingPackagesManagement } from '@/composables/useOutgoingPackagesManagement'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'
import PackageViewModal from '@/components/shipment/PackageViewModal.vue'
import PrintPreviewModal from '@/components/shipment/PrintPreviewModal.vue'
import { useAbility } from '@/plugins/casl/composables/useAbility'
import { useSuperadmin } from '@/composables/useSuperadmin'
import { getStatusColor } from '@/utils/statusUtils'

// Make URL available in the template
const URL = window.URL || window.webkitURL

const { t, locale } = useI18n()
const router = useRouter()
const { success, error } = useSnackbar()
const { packages, packagesMeta, fetchPackages, deletePackage: removePackage, deleteMultiplePackages, loading } = useOutgoingPackagesManagement()

// Pagination variables
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const totalPages = ref(1)

const { can, isAdmin, getTeamBadgeColor } = useAbility()

const { isSuperadmin } = useSuperadmin()

// Team managers can read packages, admins can edit, but ONLY superadmin
// can delete (matches RestrictDeletesToSuperadmin middleware on the backend).
const canEditPackage = computed(() => can('edit', 'Packages') || isAdmin.value)
const canDeletePackage = computed(() => isSuperadmin.value)
const canReadPackage = computed(() => can('read', 'Packages'))
const canCreatePackingList = computed(() => can('create', 'Packinglist'))

// Function to map frontend locale to backend locale
const mapLocale = frontendLocale => {
  // Map frontend locale codes to backend locale codes
  const localeMap = {
    'zh': 'ce', // Chinese in frontend is 'zh', but backend uses 'ce'
    'cn': 'ce', // Alternative Chinese code
    'fr': 'fr', // French is same in both
    'en': 'en'  // English is same in both
  }
  
  // Return the mapped locale or the original if no mapping exists
  return localeMap[frontendLocale] || frontendLocale
}

const showPrintPreview = ref(false)

// Function to get localized product name from a translations object
const getLocalizedProductName = (product) => {
  if (!product) return '-'
  
  // If the product name is a string, return it directly
  if (typeof product.name === 'string') return product.name
  
  // If the product has name translations as an object
  if (product.name && typeof product.name === 'object') {
    const backendLocale = mapLocale(locale.value)
    
    // Try to get the name in the current locale
    if (product.name[backendLocale]) {
      return product.name[backendLocale]
    }
    
    // Fall back to English if available
    if (product.name.en) {
      return product.name.en
    }
    
    // Fall back to Chinese if available
    if (product.name.ce) {
      return product.name.ce
    }
    
    // Fall back to the first available translation
    const firstKey = Object.keys(product.name)[0]
    if (firstKey) {
      return product.name[firstKey]
    }
  }
  
  // If we can't find a translation, return '-'
  return '-'
}

// Get localized products display text
const getLocalizedProductsDisplayText = (item) => {
  // If no products, return dash
  if (!item || 
      (!item.products && !item.products_display_details) || 
      (item.products && item.products.length === 0 && (!item.products_display_details || item.products_display_details.length === 0))) {
    return '-'
  }
  
  // If we have products with translations
  if (item.products && item.products.length > 0) {
    return item.products.map(product => {
      const name = getLocalizedProductName(product)
      return `${name} (x${product.quantity})`
    }).join(', ')
  }
  
  // If we have products_display_details (already formatted)
  if (item.products_display_details && item.products_display_details.length > 0) {
    return item.products_display_details.map(product => {
      const name = typeof product.name === 'object' 
        ? getLocalizedProductName(product) 
        : product.name
      return `${name} (x${product.quantity})`
    }).join(', ')
  }
  
  // Fall back to display text
  return item.products_display_text || '-'
}

const uploadedImage = ref(null)
const uploadingImage = ref(false)
const selectedPackageForView = ref(null)
const viewDialogOpen = ref(false)
const showUploadImageDialog = ref(false)
const selectedRows = ref([])
const search = ref('')
const showConfirmDeleteDialog = ref(false)
const expandedPanels = ref([0])

// Add to packing list state
const showAddToPackingListDialog = ref(false)
const selectedPackingListId = ref(null)
const packingListOptions = ref([])
const loadingPackingLists = ref(false)
const addingToPackingList = ref(false)

// Delete Dialog State
const deleteDialog = ref({
  open: false,
  loading: false,
  item: null,
})

const rawFilterData = ref({
  statusOptions: [],
  transportOptions: [],
  teamOptions: [],
})

// Define computed properties for all translated options
const statusOptions = computed(() => {
  if (!rawFilterData.value.statusOptions.length) {
    return [
      { title: t('shipment.statuses.pending'), value: 'pending' },
      { title: t('shipment.statuses.packaged'), value: 'packaged' },
      { title: t('shipment.statuses.shipped'), value: 'shipped' },
      { title: t('shipment.statuses.loaded'), value: 'loaded' },
      { title: t('shipment.statuses.completed'), value: 'completed' },
    ]
  }

  return rawFilterData.value.statusOptions.map(status => ({
    value: status.value,
    title: t(`shipment.statuses.${status.title}`),
  }))
})

const transportOptions = computed(() => {
  if (!rawFilterData.value.transportOptions.length) {
    return [
      { title: t('filters.all'), value: null },
      { title: t('shipment.by_plane'), value: 0 },
      { title: t('shipment.by_ship'), value: 1 },
    ]
  }

  return [
    { title: t('filters.all'), value: 'all' },
    ...rawFilterData.value.transportOptions.map(transport => ({
      value: transport.value,
      title: t(`shipment.${transport.title}`),
    })),
  ]
})

const teamOptions = computed(() => {
  if (!rawFilterData.value.teamOptions || !rawFilterData.value.teamOptions.length) {
    return [] // Return empty array if no teams loaded from API
  }

  return rawFilterData.value.teamOptions.map(team => ({
    value: team.value,
    title: team.title,
    code: team.code
  }))
})

// Helper function to get first and last day of current month
const getCurrentMonthDates = () => {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  // Format as YYYY-MM-DD (internal formatting for API parameters)
  const formatDateYMD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  return {
    start: formatDateYMD(firstDay),
    end: formatDateYMD(lastDay),
  }
}

// Get current year dates
const getCurrentYearDates = () => {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), 0, 1) // Jan 1
  const lastDay = new Date(now.getFullYear(), 11, 31) // Dec 31

  // Format as YYYY-MM-DD (internal formatting for API parameters)
  const formatDateYMD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  return {
    start: formatDateYMD(firstDay),
    end: formatDateYMD(lastDay),
  }
}

// Get current month dates
const currentMonth = getCurrentMonthDates()

// Get current year dates
const currentYear = getCurrentYearDates()

// Filter values with defaults
const filters = reactive({
  status: '',
  team_id: null, // No default team selected
  transport: 1, // Default to all
  start_date: currentYear.start,
  end_date: currentYear.end,
  date_preset: 'this_year',
})

// Helper function to format date according to locale
const formatDate = dateString => {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)

    // Get current locale from i18n
    const { locale } = useI18n()
    const currentLocale = locale.value || 'en'

    // Use Intl.DateTimeFormat with the current i18n locale for consistent formatting
    return new Intl.DateTimeFormat(currentLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  } catch (e) {
    // Return the original string if there's an error
    return dateString
  }
}

// Fetch filter options
const fetchFilterOptions = async () => {
  try {
    // Make API call to get server options
    const response = await axios.get('/shipment/outgoing-packages/filter-options')

    if (response.data) {
      // Store raw data from API
      if (response.data.statuses) {
        rawFilterData.value.statusOptions = Object.entries(response.data.statuses)
          .map(([value, title]) => ({ value, title }))
      }

      if (response.data.transports) {
        rawFilterData.value.transportOptions = response.data.transports
      }

      if (response.data.teams) {
        rawFilterData.value.teamOptions = response.data.teams
      }
    }
  } catch (error) {
    console.error('Error fetching filter options:', error)

    // Default options will be used via computed properties
  }
}

// Date preset options
const datePresets = computed(() => [
  { title: t('filters.today'), value: 'today' },
  { title: t('filters.yesterday'), value: 'yesterday' },
  { title: t('filters.thisWeek'), value: 'this_week' },
  { title: t('filters.lastWeek'), value: 'last_week' },
  { title: t('filters.thisMonth'), value: 'current_month' },
  { title: t('filters.lastMonth'), value: 'last_month' },
  { title: t('filters.thisYear'), value: 'this_year' },
  { title: t('filters.lastYear'), value: 'last_year' },
  { title: t('filters.custom'), value: 'custom' },
])

// Apply filters
const applyFilters = () => {
  // Create params object
  const params = {
    include_translations: true // Always include translations
  }

  // Only add defined filters to avoid sending undefined values
  if (filters.status && filters.status !== 'all') {
    params.status = filters.status
  }

  if (filters.team_id !== null && filters.team_id !== undefined) {
    params.team_id = filters.team_id
  }

  if (filters.transport !== null && filters.transport !== 'all') {  // ✅ Ensure transport is added
    params.transport = Number(filters.transport) // ✅ Convert to number
  }

  if (filters.start_date) {
    params.start_date = filters.start_date
  }

  if (filters.end_date) {
    params.end_date = filters.end_date
  }

  if (search.value) {
    params.search = search.value
    params.search_products = search.value // Also search in products
  }

  // Add pagination parameters
  params.page = currentPage.value
  params.per_page = itemsPerPage.value

  // Pass-through column sort to the API (allowlisted server-side).
  if (sortBy.value) {
    params.sort_by = sortBy.value
    params.sort_order = sortOrder.value
  }

  // Apply filters and fetch packages
  fetchPackages(params).then(data => {
    // Update pagination metadata
    totalItems.value = packagesMeta.value.total || 0;
    totalPages.value = packagesMeta.value.last_page || 1;
  }).catch(err => {
    error(t('messages.fetchError', 'Error fetching packages'))
  })
}

// Handle date preset selection
const handleDatePresetChange = preset => {
  const now = new Date()
  let start, end

  switch(preset) {
  case 'today':
    start = end = new Date(now)
    break

  case 'yesterday':
    start = end = new Date(now)
    start.setDate(start.getDate() - 1)
    end.setDate(end.getDate() - 1)
    break

  case 'this_week':
    start = new Date(now)
    start.setDate(now.getDate() - now.getDay()) // Start of week (Sunday)
    end = new Date(now)
    break

  case 'last_week':
    start = new Date(now)
    start.setDate(now.getDate() - now.getDay() - 7) // Start of last week
    end = new Date(start)
    end.setDate(start.getDate() + 6) // End of last week
    break

  case 'current_month':
    start = new Date(now.getFullYear(), now.getMonth(), 1)
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    break

  case 'last_month':
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    end = new Date(now.getFullYear(), now.getMonth(), 0)
    break

  case 'this_year':
    start = new Date(now.getFullYear(), 0, 1)  // January 1
    end = new Date(now.getFullYear(), 11, 31) // December 31
    break

  case 'last_year':
    start = new Date(now.getFullYear() - 1, 0, 1)  // January 1 last year
    end = new Date(now.getFullYear() - 1, 11, 31) // December 31 last year
    break
  case 'custom':
    // Don't set dates automatically, let user pick them
    return
  }

  // Format dates
  const formatDate = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  // Set date values
  filters.start_date = formatDate(start)
  filters.end_date = formatDate(end)

  // Apply filters
  applyFilters()
}

// Watch date preset changes
watch(() => filters.date_preset, newValue => {
  if (newValue) {
    handleDatePresetChange(newValue)
  }
})

// Reset filters
const resetFilters = () => {
  // Reset to default values
  filters.status = ''
  filters.team_id = null // No default team selected
  filters.transport = 1
  filters.date_preset = 'this_year'

  // Current year dates will be set by the watcher
  search.value = ''
  applyFilters()
}

// Watch for filter changes
watch([search, filters], () => {
  applyFilters()
}, { deep: true })

// Lifecycle hooks
onMounted(async () => {
  // Check if user can read packages or is admin
  if (!can('read', 'Packages') && !isAdmin.value) {
    error('No permission to view packages')
    router.push('/unauthorized')
    return
  }

  fetchFilterOptions()

  // Apply default filters
  await applyFilters()
  await nextTick()
  pageReady.value = true

  // Add event listener for package updates
  window.addEventListener('package:updated', handlePackageUpdated)
})

onUnmounted(() => {
  window.removeEventListener('package:updated', handlePackageUpdated)
})

// Handler function for the custom event
const handlePackageUpdated = event => {
  // Refresh the data
  applyFilters()
}

// Computed Headers
const headers = computed(() => [
  { title: t('tableHeaders.image'), key: 'image', align: 'center', sortable: false },
  { title: t('tableHeaders.barcode'), key: 'barcode', align: 'start' },
  { title: t('tableHeaders.date'), key: 'date', align: 'center' },
  { title: t('tableHeaders.status'), key: 'status', align: 'center' },
  { title: t('tableHeaders.parcels'), key: 'parcels', align: 'start' },
  { title: t('tableHeaders.products'), key: 'products', align: 'start' },
  { title: t('shipment.team'), key: 'team', align: 'center', sortable: false },
  { title: t('tableHeaders.transport'), key: 'transport', align: 'center' },
  { title: t('tableHeaders.weight'), key: 'weight', align: 'end' },
  { title: t('tableHeaders.volume'), key: 'volume', align: 'end' },
  { title: t('tableHeaders.actions'), key: 'actions', align: 'center', sortable: false },
])

// View package
const viewPackage = pkg => {
  // Note: we don't need to set include_translations here since
  // the API request is already configured to include translations
  selectedPackageForView.value = pkg
  viewDialogOpen.value = true
}

// Handle image upload request
const handleUploadImageRequest = () => {
  if (selectedPackageForView.value && selectedPackageForView.value.id) {
    viewDialogOpen.value = false
    showUploadImageDialog.value = true
  }
}

// Handle image click based on whether the package has an image
const handleImageClick = pkg => {
  // Close any open dialogs first
  viewDialogOpen.value = false
  showUploadImageDialog.value = false

  // Set the selected package
  selectedPackageForView.value = pkg

  // Use a small delay to ensure dialogs are fully closed before opening a new one
  setTimeout(() => {
    if (pkg.custom_image_url && !pkg.custom_image_url.includes('default.png')) {
      // If the package has an image, show the view dialog
      viewDialogOpen.value = true
    } else {
      // If the package has no image, show the upload dialog
      showUploadImageDialog.value = true
    }
  }, 50)
}

// Handle image upload
const handleImageUpload = event => {
  if (!event.target.files || event.target.files.length === 0) {
    uploadedImage.value = null
    
    return
  }

  uploadedImage.value = event.target.files[0]
}

// Upload image to server
const uploadImage = async () => {
  if (!uploadedImage.value || !selectedPackageForView.value || !selectedPackageForView.value.id) {
    error(t('messages.missingPackageOrImage', 'Missing package ID or image'))
    
    return
  }

  uploadingImage.value = true

  try {
    const formData = new FormData()

    formData.append('package_id', selectedPackageForView.value.id)
    formData.append('image', uploadedImage.value)

    // Get auth token from cookie
    const token = document.cookie.split('; ')
      .find(row => row.startsWith('accessToken='))
      ?.split('=')[1]

    // Upload image using API endpoint
    const response = await axios.post('/shipment/outgoing-packages/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (response.data.success) {
      // Reset the form and close the dialog
      uploadedImage.value = null
      showUploadImageDialog.value = false

      // Refresh the data
      applyFilters()

      // Show success notification
      success(t('messages.imageUploaded', 'Image uploaded successfully'))
    } else {
      error(response.data.message || t('messages.uploadError', 'Failed to upload image'))
    }
  } catch (err) {
    console.error('Upload error:', err.response || err)
    error(err.response?.data?.message || t('messages.uploadError', 'Failed to upload image'))
  } finally {
    uploadingImage.value = false
  }
}

// Confirm delete package
const confirmDelete = item => {
  deleteDialog.value.item = item
  deleteDialog.value.open = true
}

// Delete package
const deletePackage = async () => {
  if (!deleteDialog.value.item) return

  deleteDialog.value.loading = true
  try {
    await removePackage(deleteDialog.value.item.id)

    // Refresh the packages
    applyFilters()

    // Show success message
    success(t('messages.packageDeleted', 'Package deleted successfully'))
  } catch (err) {
    console.error('Delete error:', err)
    error(t('messages.deleteError', 'Failed to delete package'))
  } finally {
    deleteDialog.value.loading = false
    deleteDialog.value.open = false
    deleteDialog.value.item = null
  }
}

// Confirm delete selected rows
const confirmMassDelete = () => {
  if (!selectedRows.value.length) return
  showConfirmDeleteDialog.value = true
}

// Delete Selected Rows
const handleDeleteSelected = async () => {
  if (!selectedRows.value.length) return

  try {
    // Extract IDs from selected rows (selectedRows contains full objects)
    const ids = selectedRows.value.map(row => row.id)
    await deleteMultiplePackages(ids)

    showConfirmDeleteDialog.value = false
    selectedRows.value = []

    // Refresh the data to reflect deleted items
    await applyFilters()

    // Show success message
    success(t('messages.packagesDeleted', 'Packages deleted successfully'))
  } catch (err) {
    console.error('Error in handleDeleteSelected:', err)
    error(t('messages.deleteError', 'Error deleting packages'))
  }
}

// Print label for a single package
const printLabel = itemId => {
  const singleItem = packages.value.find(pkg => pkg.id === itemId)
  if (!singleItem) return
  
  printSelectedItems.value = [singleItem]
  showPrintPreview.value = true
}

// Print labels for selected packages
const printSelectedLabels = () => {
  if (!selectedRows.value.length) return
  
  console.log('selectedRows.value :', selectedRows.value)
  
  printSelectedItems.value = selectedRows.value
  console.log('selectedRows.value :', selectedRows.value)
  showPrintPreview.value = true
}

const printSelectedItems = ref([])


const handlePrint = () => {
  console.log('Impression des packages:', printSelectedItems.value)
  showPrintPreview.value = false
}

const printTableColumns = computed(() => [
  { key: 'index', title: '#', value: 'index' },
  { key: 'image', title: t('shipment.image'), value: 'image_url' },
  { key: 'barcode', title: t('shipment.barcode'), value: 'barcode' },
  { key: 'sku', title: t('shipment.sku'), value: 'sku' },
  { key: 'marking', title: t('shipment.marking'), value: 'marking' },
  { key: 'customer', title: t('shipment.customer'), value: 'incoming_parcels.0.customer.name' },
  { key: 'transport', title: t('shipment.transport'), value: 'transport_display' },
  { key: 'belongs_to', title: t('shipment.belongs_to'), value: 'belongs_to_display' },
  { key: 'weight', title: t('shipment.weight'), value: 'weight', format: 'weight' },
  { key: 'dimensions', title: t('shipment.dimensions'), value: 'dimensions', format: 'dimensions' },
  { key: 'volume', title: t('shipment.volume'), value: 'volume' },
  { key: 'products', title: t('shipment.products'), value: 'products_display_text' },
  { key: 'status', title: t('shipment.status'), value: 'status' },
  { key: 'created_at', title: t('shipment.created_at'), value: 'created_at', format: 'date' }
])

const printTitle = computed(() => {
  if (printSelectedItems.value.length === 1) {
    return t('actions.printLabel') + ` - ${printSelectedItems.value[0].barcode}`
  }
  
  return t('actions.printLabels') + ` (${printSelectedItems.value.length})`
})


// Create packing list from selected packages
const createPackingList = () => {
  if (!selectedRows.value.length) return

  router.push({
    path: '/shipment/packing-lists/create',
    query: { package_ids: selectedRows.value.join(',') },
  })
}

// Handle VDataTableServer options update
const handleTableOptionsUpdate = options => {
  // Skip the table's first emission on mount; onMounted drives the
  // initial fetch. Without this, /outgoing-packages fires twice on load.
  if (!pageReady.value) return
  if (options.page !== currentPage.value) {
    currentPage.value = options.page
  }
  if (options.itemsPerPage !== itemsPerPage.value) {
    itemsPerPage.value = options.itemsPerPage
    currentPage.value = 1 // Reset to first page when changing items per page
  }
  if (options.sortBy && options.sortBy.length > 0) {
    sortBy.value = options.sortBy[0].key
    sortOrder.value = options.sortBy[0].order || 'asc'
  } else {
    sortBy.value = null
    sortOrder.value = 'desc'
  }
  applyFilters()
}

// Duplicate-fetch guard, see incoming-parcels/index.vue for rationale.
const pageReady = ref(false)

// Server-side sort state. Default empty so the backend's "created_at desc"
// kicks in. User-driven header clicks populate this and override.
const sortBy = ref(null)
const sortOrder = ref('desc')
// Two-way bound to VDataTableServer's sort-by — keeps the visible header
// arrow in sync with what we actually request.
const tableSort = ref([])

// Function to handle page changes
const onPageChange = page => {
  currentPage.value = page
  applyFilters()
}

// Function to handle items per page changes
const onItemsPerPageChange = perPage => {
  itemsPerPage.value = perPage
  currentPage.value = 1 // Reset to first page when changing items per page
  applyFilters()
}

// Helper functions for team lookup
const getTeamNameById = (teamId) => {
  const team = teamOptions.value.find(team => team.value === teamId)
  return team ? team.title : 'Unknown Team'
}

const getTeamCodeById = (teamId) => {
  const team = teamOptions.value.find(team => team.value === teamId)
  return team ? team.code : 'UNK'
}

// Legacy team name mapping
const getLegacyTeamName = (belongsTo) => {
  const legacyTeamMap = {
    0: 'Going Beyond',
    1: 'Godana', 
    2: 'Je Prends'
  }
  return legacyTeamMap[belongsTo] || 'Unknown'
}

const getLegacyTeamCode = (belongsTo) => {
  const legacyTeamCodeMap = {
    0: 'GB',
    1: 'GD',
    2: 'JP'
  }
  return legacyTeamCodeMap[belongsTo] || 'UNK'
}

// Fetch packing lists when dialog is opened
watch(showAddToPackingListDialog, async (newVal) => {
  if (newVal) {
    await fetchPackingLists()
  } else {
    selectedPackingListId.value = null
  }
})

// Fetch available packing lists
const fetchPackingLists = async () => {
  loadingPackingLists.value = true
  try {
    const response = await axios.get('/shipment/packing-lists', {
      params: {
        per_page: -1, // Get all packing lists
      }
    })

    if (response.data && response.data.data) {
      packingListOptions.value = response.data.data.map(pl => {
        // Use reference_code (e.g. "1.4_SEA") as the user-facing identifier;
        // never expose container_number / BL number in non-admin views.
        const containerLabel = pl.reference_code || pl.container_arrival?.reference_code || `#${pl.container_arrival?.id ?? '?'}`

        // Format date with time
        let formattedDate = 'N/A'
        if (pl.created_at) {
          try {
            const date = new Date(pl.created_at)
            formattedDate = new Intl.DateTimeFormat(locale.value || 'en', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(date)
          } catch (e) {
            formattedDate = pl.created_at
          }
        }

        return {
          value: pl.id,
          label: `#${pl.id} - ${containerLabel}`,
          container: containerLabel,
          date: formattedDate,
          raw: pl
        }
      })
    }
  } catch (err) {
    console.error('Error fetching packing lists:', err)
    error(t('messages.fetchError', 'Failed to fetch packing lists'))
  } finally {
    loadingPackingLists.value = false
  }
}

// Add selected packages to existing packing list
const addToExistingPackingList = async () => {
  if (!selectedPackingListId.value || !selectedRows.value.length) return

  addingToPackingList.value = true
  try {
    const packageIds = selectedRows.value.map(row => row.id)
    
    const response = await axios.post(
      `/shipment/packing-lists/${selectedPackingListId.value}/add-packages`,
      { package_ids: packageIds }
    )

    if (response.data.success) {
      success(response.data.message)
      
      // Clear selection and close dialog
      selectedRows.value = []
      showAddToPackingListDialog.value = false
      
      // Refresh the packages list
      await applyFilters()
    } else {
      error(response.data.message || t('messages.addError', 'Failed to add packages'))
    }
  } catch (err) {
    console.error('Error adding packages to packing list:', err)
    error(
      err.response?.data?.message ||
      t('messages.addError', 'Failed to add packages to packing list')
    )
  } finally {
    addingToPackingList.value = false
  }
}
</script>

<style scoped>
.v-table {
  width: 100%;
}

.image-cell {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
}

.image-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 2px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.image-preview {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.image-preview:hover {
  transform: scale(1.05);
}

.image-preview img {
  object-fit: cover;
}

.products-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.outgoing-packages-print-modal {
  z-index: 9999;
}

.outgoing-packages-print-modal .print-preview-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.outgoing-packages-print-modal .cell-barcode {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #007bff;
}

.outgoing-packages-print-modal .cell-customer {
  font-weight: 500;
  color: #28a745;
}

.outgoing-packages-print-modal .cell-products {
  font-style: italic;
  color: #6c757d;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
